import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Clock, MapPin, Calendar, Ticket } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { resolveImageUrl } from '../lib/imageUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const peso = (n) => `\u20B1${Number(n || 0).toLocaleString('en-PH')}`;

const Events = () => {
  const [events, setEvents] = useState([]);
  const [expandedDesc, setExpandedDesc] = useState(new Set());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [agreedTerms, setAgreedTerms] = useState(new Set());
  const [bookingData, setBookingData] = useState({
    customer_name: '', customer_email: '', customer_phone: '', notes: ''
  });
  const navigate = useNavigate();

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
    }
  };

  const toggleDesc = (id) => {
    setExpandedDesc((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const openBooking = (event) => {
    setSelectedEvent(event);
    setSelectedAddons([]);
    setAgreedTerms(new Set());
    setBookingData({ customer_name: '', customer_email: '', customer_phone: '', notes: '' });
  };

  const toggleTerm = (id) => {
    setAgreedTerms((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const eventTerms = () => (selectedEvent?.terms || []).filter((t) => t && (t.text || '').trim());
  const allTermsAgreed = () => eventTerms().filter((t) => t.required !== false).every((t) => agreedTerms.has(t.id));

  const toggleAddon = (id) => {
    if (selectedEvent?.addon_selection === 'single') {
      setSelectedAddons((prev) => (prev.includes(id) ? [] : [id]));
    } else {
      setSelectedAddons((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    }
  };

  const handleInputChange = (e) => setBookingData({ ...bookingData, [e.target.name]: e.target.value });

  const addonTotal = () => {
    if (!selectedEvent) return 0;
    return (selectedEvent.add_ons || [])
      .filter((a) => selectedAddons.includes(a.id))
      .reduce((sum, a) => sum + Number(a.price || 0), 0);
  };

  const grandTotal = () => (selectedEvent ? Number(selectedEvent.price || 0) + addonTotal() : 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!bookingData.customer_name || !bookingData.customer_email || !bookingData.customer_phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!allTermsAgreed()) {
      toast.error('Please agree to the required terms & conditions');
      return;
    }
    try {
      const response = await axios.post(`${API}/bookings`, {
        ...bookingData,
        booking_type: 'event',
        event_id: selectedEvent.id,
        booking_date: selectedEvent.event_date,
        booking_time: selectedEvent.time,
        selected_addon_ids: selectedAddons,
        accepted_term_ids: [...agreedTerms],
      });
      toast.success('Ticket reserved! Redirecting to payment...');
      setTimeout(() => navigate(`/payment/${response.data.id}`), 1000);
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error(error.response?.data?.detail || 'Failed to reserve ticket');
    }
  };

  const formatDate = (d) =>
    new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  const formatTime = (t) => {
    if (!t || !t.includes(':')) return t;
    const [h, m] = t.split(':').map(Number);
    if (Number.isNaN(h)) return t;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hr = h % 12 || 12;
    return `${hr}:${String(m || 0).padStart(2, '0')} ${ampm}`;
  };

  return (
    <div className="min-h-screen pt-32 pb-16">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="mb-16" data-testid="events-header">
          <h1 className="text-4xl md:text-6xl font-bold tracking-tighter mb-4" style={{ fontFamily: 'var(--heading-font)' }}>
            EVENTS
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-zinc-600">
            Grab your tickets for special events, showcases, or performances
          </p>
        </div>

        {events.length === 0 ? (
          <p className="text-zinc-500" data-testid="events-empty">No upcoming events right now. Check back soon!</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event) => {
              const isOpen = expandedDesc.has(event.id);
              const longDesc = (event.description || '').length > 140;
              return (
                <div
                  key={event.id}
                  className="bg-white border border-zinc-100 overflow-hidden hover:border-black transition-all duration-300 studio-card flex flex-col"
                  data-testid={`event-card-${event.id}`}
                >
                  <div className="aspect-[3/4] bg-cover bg-center bg-zinc-100" style={{ backgroundImage: `url('${resolveImageUrl(event.image_url)}')` }} />
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'var(--heading-font)' }}>
                      {event.name}
                    </h3>
                    <p className={`text-sm text-zinc-600 mb-1 whitespace-pre-line ${isOpen ? '' : 'line-clamp-2'}`} data-testid={`event-desc-${event.id}`}>
                      {event.description}
                    </p>
                    {longDesc && (
                      <button
                        onClick={() => toggleDesc(event.id)}
                        className="text-sm font-medium text-black underline self-start mb-3"
                        data-testid={`event-see-more-${event.id}`}
                      >
                        {isOpen ? 'See less' : 'See more'}
                      </button>
                    )}

                    <div className="space-y-2 mb-4 text-sm mt-auto">
                      <div className="flex items-center gap-2 text-zinc-700">
                        <Calendar size={16} />
                        <span>{formatDate(event.event_date)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-zinc-700">
                        <Clock size={16} />
                        <span>{formatTime(event.time)}{event.end_time ? ` – ${formatTime(event.end_time)}` : ''}</span>
                      </div>
                      {event.location && (
                        <div className="flex items-center gap-2 text-zinc-700">
                          <MapPin size={16} />
                          <span>{event.location}</span>
                        </div>
                      )}
                      <div className="text-zinc-900 font-bold text-base" data-testid={`event-price-${event.id}`}>
                        {peso(event.price)}
                      </div>
                    </div>

                    {event.sold_out ? (
                      <button disabled className="w-full bg-zinc-200 text-zinc-500 rounded-full px-6 py-3 font-bold cursor-not-allowed" data-testid={`event-sold-out-${event.id}`}>
                        SOLD OUT
                      </button>
                    ) : (
                      <button
                        onClick={() => openBooking(event)}
                        className="w-full bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-6 py-3 font-bold transition-transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
                        data-testid={`buy-ticket-btn-${event.id}`}
                      >
                        <Ticket size={18} /> BUY TICKET
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Booking Dialog */}
      <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="event-booking-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>
              {selectedEvent?.name}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="customer_name">Full Name *</Label>
              <Input id="customer_name" name="customer_name" value={bookingData.customer_name} onChange={handleInputChange} required data-testid="event-name-input" />
            </div>
            <div>
              <Label htmlFor="customer_email">Email *</Label>
              <Input id="customer_email" name="customer_email" type="email" value={bookingData.customer_email} onChange={handleInputChange} required data-testid="event-email-input" />
            </div>
            <div>
              <Label htmlFor="customer_phone">Phone Number *</Label>
              <Input id="customer_phone" name="customer_phone" value={bookingData.customer_phone} onChange={handleInputChange} required data-testid="event-phone-input" />
            </div>
            <div>
              <Label>Event Date</Label>
              <Input type="text" value={selectedEvent ? `${formatDate(selectedEvent.event_date)} • ${formatTime(selectedEvent.time)}` : ''} readOnly disabled data-testid="event-date-display" />
              <p className="text-xs text-zinc-500 mt-1">Auto-filled from the event schedule.</p>
            </div>

            {selectedEvent?.add_ons?.length > 0 && (
              <div data-testid="event-addons">
                <Label>Add-ons{selectedEvent.addon_selection === 'single' ? ' (choose one)' : ''}</Label>
                <div className="space-y-2 mt-1 border border-zinc-200 rounded p-3">
                  {selectedEvent.add_ons.map((a) => (
                    <label key={a.id} className="flex items-center justify-between gap-2 cursor-pointer text-sm" data-testid={`event-addon-${a.id}`}>
                      <span className="flex items-center gap-2">
                        <input
                          type={selectedEvent.addon_selection === 'single' ? 'radio' : 'checkbox'}
                          name={selectedEvent.addon_selection === 'single' ? 'event-addon' : undefined}
                          checked={selectedAddons.includes(a.id)}
                          onChange={() => toggleAddon(a.id)}
                          className="w-4 h-4 accent-black"
                          data-testid={`event-addon-checkbox-${a.id}`}
                        />
                        {a.name}
                      </span>
                      <span className="text-zinc-600">{a.price ? `+ ${peso(a.price)}` : 'Free'}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Input id="notes" name="notes" value={bookingData.notes} onChange={handleInputChange} data-testid="event-notes-input" />
            </div>

            <div className="bg-zinc-50 p-4 rounded flex justify-between items-center" data-testid="event-total-box">
              <span className="font-medium">Total Amount:</span>
              <span className="text-2xl font-bold" style={{ fontFamily: 'var(--heading-font)' }} data-testid="event-total-amount">{peso(grandTotal())}</span>
            </div>

            {eventTerms().length > 0 && (
              <div className="pt-2 border-t border-zinc-200" data-testid="event-terms">
                <p className="font-bold text-sm mb-1" style={{ fontFamily: 'var(--heading-font)' }}>Terms &amp; Conditions</p>
                <p className="text-xs text-zinc-500 mb-3">Please review the following. Required items must be agreed to proceed.</p>
                <div className="space-y-3">
                  {eventTerms().map((t) => (
                    <label key={t.id} className="flex gap-3 items-start cursor-pointer" data-testid={`event-term-label-${t.id}`}>
                      <input
                        type="checkbox"
                        checked={agreedTerms.has(t.id)}
                        onChange={() => toggleTerm(t.id)}
                        className="mt-1 w-4 h-4 accent-black shrink-0"
                        data-testid={`event-term-${t.id}`}
                      />
                      <span className="text-xs text-zinc-700 whitespace-pre-line">
                        {t.text}
                        {t.required !== false
                          ? <span className="text-red-600 font-bold"> *</span>
                          : <span className="text-zinc-400 italic"> (optional)</span>}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-2 space-y-2">
              <button
                type="submit"
                disabled={!allTermsAgreed()}
                className="w-full bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-6 py-3 font-bold transition-transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                data-testid="event-confirm-btn"
              >
                PROCEED TO PAYMENT
              </button>
              <button type="button" onClick={() => setSelectedEvent(null)} className="w-full border-2 border-black text-black hover:bg-black hover:text-white rounded-full px-6 py-3 transition-colors" data-testid="event-cancel-btn">
                CANCEL
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Events;
